import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Briefcase, MapPin, ShieldCheck, Sparkles } from "lucide-react";

const Detail = ({ label, value, testId }) => (
  <div data-testid={testId} className="border-t border-[#E4E7EA] pt-3">
    <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8]">{label}</p>
    <p className="mt-1 text-sm font-semibold text-[#0D2F4D]">{value || "Not provided"}</p>
  </div>
);

export const ShortlistPreview = ({ open, onOpenChange, data }) => {
  const p = data?.profile || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="employer-profile-preview-modal" className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 border-0">
        <DialogHeader className="bg-[#0D2F4D] px-6 py-5 text-left">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#C6D21F]">Employer-facing preview</p>
              <DialogTitle className="mt-1 font-display text-2xl text-white">The Ditch Shortlist</DialogTitle>
            </div>
            <Badge data-testid="shortlist-preview-reference" className="bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#C6D21F] font-bold">{p.public_reference || "Pending reference"}</Badge>
          </div>
        </DialogHeader>
        <div className="p-6 space-y-7 bg-white">
          <div className="flex items-start gap-3 rounded-md border-l-4 border-[#C6D21F] bg-[#F2F2EF] p-4" data-testid="shortlist-preview-privacy-notice">
            <ShieldCheck className="mt-0.5 text-[#0D2F4D]" size={18} />
            <p className="text-sm text-[#0D2F4D]">Identity, contact details, CV and recruiter notes are excluded from this profile.</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-[#6F8FA8] text-xs font-bold uppercase tracking-widest"><Briefcase size={14}/> Anonymous candidate profile</div>
            <h2 data-testid="shortlist-preview-headline" className="font-display text-3xl font-extrabold text-[#0D2F4D] mt-3">{p.professional_headline || "Professional headline"}</h2>
            <div className="mt-3 flex flex-wrap gap-2 text-sm text-[#6F8FA8]">
              {p.industry && <span className="inline-flex items-center gap-1"><Sparkles size={13}/>{p.industry}</span>}
              {p.current_location && <span className="inline-flex items-center gap-1"><MapPin size={13}/>Currently based: {p.current_location}</span>}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8]">Professional summary</p>
            <p data-testid="shortlist-preview-summary" className="mt-2 text-base leading-7 text-[#29465E] whitespace-pre-line">{p.professional_summary || "No public summary yet."}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Detail label="Target roles" value={(p.target_roles || []).join(", ")} testId="shortlist-preview-target-roles" />
            <Detail label="Desired job cities" value={(p.preferred_locations || []).join(", ")} testId="shortlist-preview-locations" />
            <Detail label="Experience" value={p.years_experience != null ? `${p.years_experience} years` : ""} testId="shortlist-preview-experience" />
            <Detail label="Salary expectations" value={p.salary_expectations} testId="shortlist-preview-salary" />
            <Detail label="Availability" value={p.availability} testId="shortlist-preview-availability" />
            <Detail label="Employment preference" value={p.employment_preference} testId="shortlist-preview-employment" />
            <Detail label="Work rights" value={p.work_rights} testId="shortlist-preview-work-rights" />
            <Detail label="Open to relocation" value={p.open_to_relocation ? "Yes" : "No"} testId="shortlist-preview-relocation" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-2">Key skills</p>
              <div className="flex flex-wrap gap-2" data-testid="shortlist-preview-skills">
                {(p.key_skills || []).map((skill) => <span key={skill} className="rounded-full bg-[#0D2F4D] px-3 py-1 text-xs font-bold text-white">{skill}</span>)}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-2">Qualifications & licences</p>
              <ul data-testid="shortlist-preview-qualifications" className="space-y-2 text-sm text-[#29465E]">
                {(p.qualifications_licences || []).map((item) => <li key={item}>• {item}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};